import React, { useMemo, useState } from 'react'

import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import { type User } from '../../../models/User'

import { type IntegrationServiceId, getIntegrationDisplayName } from '../../../common/integrations'
import { getConnectedDocumentServiceIds, sortDocumentServiceIdsByDisplayName } from '../../../common/getConnectedDocumentServiceIds'
import { useLastUsedDocumentConnector } from '../../../common/lastUsedDocumentConnector'
import { getLinkedFolderIds } from '../../../common/linkedDocumentFolders'
import { useContactLinkedFoldersByServiceId } from '../../../common/useContactLinkedFolder'
import { DocumentFolderMapSource, useLinkContactFolder } from '../../../common/useLinkContactFolder'

import { type FileBrowserItem } from '../../../components/DocumentFileBrowser/types'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import { DocumentFileBrowserModal } from '../../../components/DocumentFileBrowser/DocumentFileBrowserModal'
import { DocumentConnectorActionButton, type DocumentConnectorActionItem } from '../../../components/DocumentConnector/DocumentConnectorActionButton'

import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetMeQuery } from '../../../store/user/user'

interface ContactDocumentBrowseButtonsProps {
  contact: User | null
  onContactUpdated: (user: User) => void
  onFilesSelected: (serviceId: IntegrationServiceId, items: FileBrowserItem[]) => void
}

export const ContactDocumentBrowseButtons: React.FC<ContactDocumentBrowseButtonsProps> = ({ contact, onContactUpdated, onFilesSelected }) => {
  const accountId = useAppSelector(selectAccountId)
  const { trackingClient } = useTracking()

  const { data: me } = useGetMeQuery({ accountId })

  const [activeServiceId, setActiveServiceId] = useState<IntegrationServiceId | null>(null)
  const [lastUsedServiceId, recordLastUsedServiceId] = useLastUsedDocumentConnector()

  const connectedServiceIds = useMemo(() => getConnectedDocumentServiceIds(me), [me])
  const { folderIdsByServiceId: linkedFoldersByServiceId, isLoading: linkedFoldersLoading } = useContactLinkedFoldersByServiceId(
    connectedServiceIds,
    contact?.id,
  )

  const advisorIntegration = activeServiceId ? me?.integrations?.find((integration) => integration.serviceId === activeServiceId) : undefined
  const linkedFolderId = activeServiceId && contact ? (getLinkedFolderIds(contact, activeServiceId)[0] ?? null) : null

  const handleLinkFolder = useLinkContactFolder(contact, DocumentFolderMapSource.IMPORT_DOCUMENT, onContactUpdated)

  if (connectedServiceIds.length === 0) {
    return null
  }

  const items: DocumentConnectorActionItem[] = sortDocumentServiceIdsByDisplayName(connectedServiceIds).map((serviceId) => ({
    key: serviceId,
    serviceId,
    label: `Browse in ${getIntegrationDisplayName(serviceId)}`,
  }))

  return (
    <>
      <div className='d-flex justify-content-center w-100'>
        <Tooltip id='browse-documents-tooltip' title={!contact ? 'Select a client first' : undefined}>
          <DocumentConnectorActionButton
            items={items}
            primaryKey={lastUsedServiceId ?? undefined}
            disabled={!contact}
            onSelect={(key) => {
              const serviceId = key as IntegrationServiceId
              recordLastUsedServiceId(serviceId)
              setActiveServiceId(serviceId)
            }}
          />
        </Tooltip>
      </div>
      {activeServiceId ? (
        <DocumentFileBrowserModal
          show
          onHide={() => setActiveServiceId(null)}
          serviceId={activeServiceId}
          selectionMode='multi-file'
          contactName={contact ? getUserDisplayName(contact) : undefined}
          initialFolderIds={linkedFoldersByServiceId[activeServiceId]}
          initialFoldersReady={!linkedFoldersLoading}
          linkedFolderId={linkedFolderId}
          onLinkFolder={contact && advisorIntegration ? handleLinkFolder : undefined}
          onFilesSelected={(items) => {
            trackingClient.track({ eventName: TrackEvent.DOCUMENT_UPLOADED_FROM_EXTERNAL_SOURCE, eventDetails: { system: activeServiceId } })
            onFilesSelected(activeServiceId, items)
          }}
        />
      ) : null}
    </>
  )
}
